/* =============================================================================
 *  feedModel — the shared, curated, scored item pipeline behind BOTH the Feed
 *  and the Wall. One ranked set; the score becomes row-order (feed) or tile-size
 *  (wall). The two surfaces are just renderers of this hook.
 *
 *  Ranking encodes the OWNER'S stated daily priorities (the spine), with bounded
 *  within-lane lift from each item's own urgency (amount / days-late / age):
 *
 *      1. client emails   2. open jobs   3. accounts payable   4. Google Ads
 *      … then everything else (receivables, approvals, drafts) — our call.
 *
 *  ACTION MODEL: items carry native-routing metadata (openKind + payload +
 *  jobStatus), NOT baked Andrei calls. The renderers wire clicks to useFeedActions
 *  — which opens the existing native modals/panels (job workspace, invoice/bill
 *  view, email panel) and does direct Firestore writes (job status) — keeping
 *  the common path programmatic + free. Andrei is a deliberate secondary action
 *  (andreiPrefill), never the default.
 *
 *  100% reused live data — no new Firestore queries. Assumes Invoice/Bills
 *  providers above (GraphiteHomePage) + app-level AppData/Approvals contexts.
 * ============================================================================= */

import React, { useMemo } from 'react';
import { useAppData } from '../../providers/AppDataProvider';
import { useInvoice } from '../../pages/invoices/contexts/InvoiceContext';
import { useBills } from '../../pages/bills/contexts/BillsContext';
import { useAttentionItems, AttentionItem } from '../../pages/home/hooks/useAttentionItems';
import { useApprovals } from '../../contexts/ApprovalsContext';
import { useEmailPanel } from '../../pages/mail/contexts/EmailPanelContext';
import { useWallOptions, emailCategoryFor } from './wallOptions';

// ── shared types ─────────────────────────────────────────────────────────────
export type FeedTone = 'alert' | 'accent' | 'warn' | 'info' | 'neutral';
export type FeedGroup = 'decide' | 'money' | 'comms' | 'pipeline' | 'ads';
export type GlyphName = 'approve' | 'shield' | 'ads' | 'draft' | 'in' | 'out' | 'mail' | 'job' | 'bill' | 'invoice';
export type OpenKind = 'job' | 'invoice' | 'bill' | 'email' | 'draft' | 'approval';

export interface FeedItem {
  id: string;
  group: FeedGroup;
  groupLabel: string;       // shown as the tile/row kicker
  glyph: GlyphName;
  tone: FeedTone;
  title: string;
  subtitle: string;
  meta?: string;            // right-aligned (age)
  badge?: string;           // read-only label (non-job kinds)
  preview?: string;         // short body snippet (emails)
  amount?: string;          // prominent figure (money cards)
  jobStatus?: string;       // present for jobs → render an inline status dropdown
  score: number;            // one ranking scale across all sources
  // action metadata — the renderer routes these through useFeedActions:
  openKind: OpenKind;       // which native modal/panel a click opens
  payload: any;             // the underlying record (AttentionItem.payload / approval)
  andreiPrefill: string;    // the deliberate "Ask Andrei" prompt (secondary action)
  andreiContextKind?: 'invoice' | 'bill' | 'job' | 'email' | 'other';
}

export interface FeedVitals {
  recvTotal: number; recvOverdue: number; recvOverdueCount: number;
  payDue: number; payOverdueCount: number;
  toReply: number; awaitingClients: number;
  decideCount: number; summaryBits: string;
}

// ── helpers (exported for the renderers) ─────────────────────────────────────
export const fmtMoney = (n: number): string => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${Math.round(v)}`;
};
export const greeting = (): string => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
};
export const todayLabel = (): string =>
  new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
const ageHours = (ms: number): number => (ms ? Math.floor((Date.now() - ms) / 3.6e6) : 0);
export const ageLabel = (ms: number): string => {
  if (!ms) return '';
  const h = ageHours(ms);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};
const isUnpaid = (status?: string): boolean => {
  const s = String(status || '').toUpperCase();
  return s !== 'PAID' && s !== 'VOIDED' && s !== 'DELETED';
};
const clean = (s: string): string => s.replace(/ \(we owe\)/, '');

// Automated / no-reply senders shouldn't count as "client" emails (a monitoring
// or notification blast isn't a client reaching out) even if their address
// happens to sit in the contact set — and they shouldn't crowd the top.
const isAutomatedSender = (addr: string): boolean => {
  if (!addr) return false;
  const [local = '', domain = ''] = addr.toLowerCase().split('@');
  // Only UNAMBIGUOUS no-reply local-parts — a real person can be "notify" or
  // "monitor" at a client domain, so those weak signals are left to the domain
  // check (avoids demoting a genuine client like notify@clientco.com).
  if (/(^|[._-])(no-?reply|do-?not-?reply|donotreply|noreply|mailer-daemon|postmaster|bounce)([._-]|$)/.test(local)) return true;
  // Known automated / monitoring / ESP sending domains.
  return /uptimerobot|pingdom|statuscake|sentry|sendgrid|mailchimp|mcsv|mcdlv|amazonses/.test(domain);
};

// One-line plain-text preview of an email body (strips HTML/entities, truncates).
// Strip HTML/entities + collapse whitespace → plain text. Shared with the
// big-tile body fetch (useEmailBodies).
export const cleanEmailText = (s: any): string => String(s || '')
  .replace(/<style[\s\S]*?<\/style>/gi, ' ').replace(/<script[\s\S]*?<\/script>/gi, ' ')
  .replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/&[a-z]+;/gi, ' ')
  .replace(/\s+/g, ' ').trim();

// Cache/identity key for an email body fetch (matches the mail client's keying).
export const emailKey = (email: any): string => String(email?.emailId || email?.id || '');

const emailPreview = (email: any): string => {
  // Richest body available in the list snapshot — full text, else stripped HTML,
  // else the short snippet. Big tiles fetch the real body on top of this.
  const best = [cleanEmailText(email?.text), cleanEmailText(email?.html), cleanEmailText(email?.message)]
    .reduce((a, b) => (b.length > a.length ? b : a), '');
  return best.slice(0, 1000);
};

// Thread identity for collapsing unread emails: normalised subject (strip the
// Re:/Fwd: chain) + sender address. Same subject + same sender = one thread.
const SUBJ_PREFIX = /^\s*(re|fwd|fw|aw|wg)\s*:\s*/i;
const normSubject = (s: any): string => {
  let x = String(s || '');
  while (SUBJ_PREFIX.test(x)) x = x.replace(SUBJ_PREFIX, '');
  return x.trim().toLowerCase();
};
const emailThreadKey = (email: any): string => {
  const subj = normSubject(email?.subject);
  const sender = String(email?.from?.[0]?.address || email?.sender || '').toLowerCase().trim();
  // No subject → key per-message, so unrelated blank-subject blasts from the same
  // sender don't collapse into one fake "· N messages" card (and a mark-read that
  // clears unrelated mail).
  return subj ? `${subj}::${sender}` : `__nosubj__${emailKey(email)}`;
};
const emailMs = (email: any): number => {
  const d = email?.createdAt;
  if (d && typeof d.getTime === 'function') return d.getTime();
  if (d && typeof d.seconds === 'number') return d.seconds * 1000;
  return 0;
};

// ── glyphs (local inline SVG — no icon-font dependency) ───────────────────────
export const GLYPHS: Record<GlyphName, string> = {
  approve: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z M8 12l2.5 2.5L16 9',
  shield: 'M12 3l8 3v6c0 4.6-3.3 7.7-8 9-4.7-1.3-8-4.4-8-9V6z',
  ads: 'M3 10v4l11 4V6L3 10z M14 8.5l5-2v11l-5-2',
  draft: 'M4 20h16 M6 16l9-9 3 3-9 9H6z',
  in: 'M12 3v11 M8 10l4 4 4-4 M5 21h14',
  out: 'M12 21V10 M8 14l4-4 4 4 M5 3h14',
  mail: 'M3 5h18v14H3z M3 6l9 7 9-7',
  job: 'M4 7h16v12H4z M9 7V5h6v2',
  bill: 'M6 3h12v18l-2.5-1.5L13 21l-2.5-1.5L8 21l-2-1.5V3z M9 8h6 M9 12h6',     // supplier docket → payable
  invoice: 'M7 3h7l4 4v14H7z M14 3v4h4 M10 13h4 M10 17h4',                      // issued document → receivable
};
export const Glyph: React.FC<{ name: GlyphName }> = ({ name }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={GLYPHS[name]} />
  </svg>
);

// ── priority lanes — the owner's stated order is the spine (higher = sooner /
//    bigger). Within-lane lift (bounded) lets a severe item climb inside its
//    lane without leapfrogging the owner's intended order. ─────────────────────
const LANE = {
  client_email:    2600,  // 1 ── emails from clients
  overdue_job:     2280,  // 2 ── open jobs (delivery)
  awaiting_job:    2180,
  due_job:         2120,
  overdue_bill:    1920,  // 3 ── accounts payable (you owe)
  due_bill:        1820,
  broker_grant:    1700,  // (else-band, but a trust-boundary decision — kept visible)
  gads:            1560,  // 4 ── Google Ads
  other_email:     1450,  // unread inbox, sender not matched to a client (below ads)
  overdue_invoice: 1360,  // … the rest (receivables — owner deprioritises chasing)
  agent_action:    1160,
  due_invoice:     1120,
  stale_draft:      920,
  low_approval:     820,
};

export function useFeedModel(): { items: FeedItem[]; vitals: FeedVitals } {
  const { customers } = useAppData();
  const { customFolders } = useEmailPanel();
  const { options: wallOptions } = useWallOptions();
  const { urgent, today, waitingOnYou, waitingOnThem } = useAttentionItems();
  const { items: approvals } = useApprovals();
  const { outstandingSummary } = useInvoice();
  const { allBills } = useBills();

  // Known-client email set (sender match → a *client* email, the owner's #1).
  const clientEmails = useMemo(() => {
    const s = new Set<string>();
    for (const c of (customers || []) as any[]) {
      if (c?.email) s.add(String(c.email).toLowerCase().trim());
      for (const ct of (c?.contacts || [])) {
        if (ct?.email) s.add(String(ct.email).toLowerCase().trim());
      }
    }
    return s;
  }, [customers]);

  // ── vitals (reused numbers) ───────────────────────────────────────────────
  const recvTotal = outstandingSummary?.totalOutstanding ?? 0;
  const recvOverdue = outstandingSummary?.overdueAmount ?? 0;
  const recvOverdueCount = outstandingSummary?.overdueCount ?? 0;
  const payables = useMemo(() => {
    const start = new Date(); start.setHours(0, 0, 0, 0);
    let due = 0, overdueCount = 0;
    for (const b of allBills || []) {
      if (!isUnpaid(b.status)) continue;
      due += Number(b.amountDue) || 0;
      const dd = b.dueDate ? new Date(b.dueDate) : null;
      if (dd && !isNaN(dd.getTime()) && dd < start) overdueCount += 1;
    }
    return { due, overdueCount };
  }, [allBills]);

  const items = useMemo<FeedItem[]>(() => {
    const out: FeedItem[] = [];

    // ── Approvals (decide / ads). Email DRAFTS excluded — they surface as comms
    //    stale_draft cards, so a draft never double-posts.
    for (const a of approvals) {
      if (a.source === 'draftEmail') continue;
      let base: number, group: FeedGroup, glyph: GlyphName, groupLabel: string, badge: string, tone: FeedTone;
      if (a.source === 'gads') {
        base = LANE.gads; group = 'ads'; glyph = 'ads'; groupLabel = 'Google Ads';
        badge = 'Ads change'; tone = a.risk === 'high' ? 'alert' : 'info';
      } else if (a.source === 'agentGrants') {
        base = LANE.broker_grant; group = 'decide'; glyph = 'shield'; groupLabel = 'Access request';
        badge = a.risk === 'high' ? 'High risk' : 'Access'; tone = a.risk === 'high' ? 'alert' : 'accent';
      } else {
        base = a.risk === 'high' ? LANE.broker_grant : LANE.agent_action;
        group = 'decide'; glyph = 'approve'; groupLabel = 'Decision';
        badge = a.risk === 'high' ? 'High risk' : 'Sign-off'; tone = a.risk === 'high' ? 'alert' : 'accent';
      }
      out.push({
        id: `appr_${a.source}_${a.id}`,
        group, groupLabel, glyph, tone,
        title: a.title, subtitle: a.summary || 'awaiting your decision',
        meta: ageLabel(a.createdAtMs), badge,
        score: base + Math.min(ageHours(a.createdAtMs), 48) + (a.risk === 'high' ? 150 : 0),
        openKind: 'approval', payload: a,
        andreiPrefill: `Tell me about this pending decision and whether I should approve it: ${a.title} — ${a.summary || ''}.`,
      });
    }

    // ── Attention items, mapped into the owner's lanes. Exhaustive switch (the
    //    default's `never` assert makes TS error if a new kind isn't mapped).
    const lift = (raw: number, cap: number) => Math.min(Math.max(raw, 0), cap);

    // ── Unread emails → ONE card per conversation. Group by thread (normalised
    //    subject + sender) so a back-and-forth ("Website Changes" / "RE:" / "FW:")
    //    collapses to a single card: latest message + a count, not three cards.
    //    Category filter (wall options) is applied per email before grouping.
    const threads = new Map<string, typeof urgent>();
    for (const it of [...urgent, ...today, ...waitingOnYou, ...waitingOnThem]) {
      if (it.kind !== 'unread_email') continue;
      const email = it.payload?.email;
      const cat = emailCategoryFor(email, customFolders);
      if (wallOptions.hiddenEmailCategories.includes(cat)) continue;
      const key = emailThreadKey(email);
      const arr = threads.get(key);
      if (arr) arr.push(it); else threads.set(key, [it]);
    }
    for (const group of Array.from(threads.values())) {
      group.sort((a, b) => emailMs(b.payload?.email) - emailMs(a.payload?.email)); // latest first
      const it = group[0];
      const email = it.payload?.email;
      const cat = emailCategoryFor(email, customFolders);
      const addr = String(email?.from?.[0]?.address || '').toLowerCase().trim();
      const automated = !!addr && isAutomatedSender(addr);
      const isClient = !!addr && !automated && clientEmails.has(addr);
      const count = group.length;
      // client → top lane; automated/no-reply → low (don't crowd the top); other → mid.
      const base = isClient ? LANE.client_email : automated ? 700 : LANE.other_email;
      out.push({
        id: it.id, group: 'comms',
        groupLabel: isClient ? 'Client email' : automated ? 'Notification' : (cat !== 'Inbox' ? cat : 'Email'),
        glyph: 'mail', tone: isClient ? 'accent' : 'info',
        title: it.title,
        subtitle: count > 1 ? `${it.subtitle} · ${count} messages` : it.subtitle,
        badge: isClient ? 'Client' : undefined,
        preview: emailPreview(email),
        score: base + lift(it.priority, 90) + (count > 1 ? 25 : 0),
        openKind: 'email',
        // Carry the whole thread so "mark read" clears every message at once.
        payload: { ...it.payload, threadEmails: group.map((g) => g.payload?.email).filter(Boolean) },
        andreiPrefill: `Help me reply to ${it.title}'s email "${String(it.subtitle).split(' · ')[0]}".`,
        andreiContextKind: 'email',
      });
    }

    // ── Remaining attention items, mapped into the owner's lanes. Exhaustive
    //    switch (the default's `never` assert errors if a new kind isn't mapped).
    //    Emails are handled above.
    for (const it of [...urgent, ...today, ...waitingOnYou, ...waitingOnThem]) {
      if (it.kind === 'unread_email') continue;
      let f: FeedItem | null = null;
      switch (it.kind) {
        case 'overdue_job':
          f = mk(it, 'pipeline', 'Open job', 'job', 'warn', 'Overdue', LANE.overdue_job, it.priority / 12, 220,
            `Draft a follow-up to ${it.title} about their overdue job (${it.subtitle}).`); break;
        case 'awaiting_client_job':
          f = mk(it, 'pipeline', 'Open job', 'job', 'neutral', 'Awaiting client', LANE.awaiting_job, it.priority, 150,
            `Draft a follow-up nudge to ${it.title} about their stalled job (${it.subtitle}).`); break;
        case 'due_job':
          f = mk(it, 'pipeline', 'Open job', 'job', 'info', 'Due today', LANE.due_job, it.priority / 12, 150,
            `What's outstanding on ${it.title}'s job due today (${it.subtitle})?`); break;
        case 'overdue_bill':
          // cap kept low so even a large/old bill stays in the payable lane,
          // below every open-job kind (owner: jobs #2 outrank payable #3).
          f = mk(it, 'money', 'Payable', 'bill', 'warn', 'You owe', LANE.overdue_bill, it.priority / 8, 150,
            `Remind me about the bill from ${it.title} (${clean(it.subtitle)}) and what to pay.`); break;
        case 'due_bill':
          f = mk(it, 'money', 'Payable', 'bill', 'info', 'Due today', LANE.due_bill, it.priority / 10, 150,
            `What do I need to pay ${it.title} (${it.subtitle})?`); break;
        case 'overdue_invoice':
          // receivables are deprioritised (owner chases less); a severe one may
          // lift toward the ads/payable band but not past it (cap 250).
          f = mk(it, 'money', 'Receivable', 'invoice', 'alert', 'Overdue', LANE.overdue_invoice, it.priority / 8, 250,
            `Draft a friendly payment reminder for ${it.title} — ${it.subtitle}.`); break;
        case 'due_invoice':
          f = mk(it, 'money', 'Receivable', 'invoice', 'info', 'Due today', LANE.due_invoice, it.priority / 10, 150,
            `Draft a friendly payment reminder for ${it.title} — ${it.subtitle}.`); break;
        case 'stale_draft':
          f = mk(it, 'comms', 'Draft', 'draft', 'neutral', undefined, LANE.stale_draft, it.priority, 80,
            `Help me finish and send the draft "${it.title}".`); break;
        default: {
          const _exhaustive: never = it.kind; void _exhaustive; // new kind? map it above.
        }
      }
      if (f) out.push(f);
    }

    return out.sort((a, b) => b.score - a.score);

    // local builder — keeps each attention case to one readable line. openKind
    // comes from the payload; jobs carry their status (→ inline dropdown) and
    // drop the read-only badge (the dropdown shows the status).
    function mk(
      it: AttentionItem, group: FeedGroup, groupLabel: string, glyph: GlyphName, tone: FeedTone,
      badge: string | undefined, base: number, rawLift: number, liftCap: number, andreiPrefill: string,
    ): FeedItem {
      const openKind = (it.payload?.kind || 'email') as OpenKind;
      const jobStatus = openKind === 'job' ? (it.payload?.job?.status || 'Open') : undefined;
      const ctxKind = openKind === 'draft' ? 'email'
        : (['invoice', 'bill', 'job', 'email'].includes(openKind) ? openKind : 'other');
      // Money cards: lift the amount out of "ref · $amount · timing" into a bold
      // figure of its own, leaving the subtitle as "ref · timing".
      let subtitle = it.subtitle;
      let amount: string | undefined;
      if (group === 'money') {
        const parts = it.subtitle.split(' · ');
        if (parts.length >= 3 && /[\d$]/.test(parts[1] || '')) {
          amount = parts[1].trim();
          subtitle = [parts[0], ...parts.slice(2)].join(' · ');
        }
      }
      return {
        id: it.id, group, groupLabel, glyph, tone,
        title: it.title, subtitle,
        badge: jobStatus ? undefined : badge,
        amount,
        jobStatus,
        score: base + lift(rawLift, liftCap),
        openKind, payload: it.payload,
        andreiPrefill, andreiContextKind: ctxKind as FeedItem['andreiContextKind'],
      };
    }
  }, [approvals, urgent, today, waitingOnYou, waitingOnThem, clientEmails, customFolders, wallOptions.hiddenEmailCategories]);

  const decideCount = approvals.filter((a) => a.source !== 'draftEmail').length;

  // Email conversations awaiting a reply (collapsed by thread + category-filtered);
  // drives the "To reply" vital. This is the true count of threads to reply to —
  // the wall now renders one tile per thread (no cap), so this matches the
  // visible email cards rather than reading higher than them.
  const visibleEmailThreads = useMemo(() => {
    const keys = new Set<string>();
    for (const it of waitingOnYou) {
      if (it.kind !== 'unread_email') continue;
      const email = it.payload?.email;
      const cat = emailCategoryFor(email, customFolders);
      if (wallOptions.hiddenEmailCategories.includes(cat)) continue;
      keys.add(emailThreadKey(email));
    }
    return keys.size;
  }, [waitingOnYou, customFolders, wallOptions.hiddenEmailCategories]);

  const summaryBits = [
    visibleEmailThreads > 0 ? `${visibleEmailThreads} to reply` : null,
    waitingOnThem.length > 0 ? `${waitingOnThem.length} open job${waitingOnThem.length === 1 ? '' : 's'} waiting` : null,
    payables.overdueCount > 0 ? `${payables.overdueCount} bill${payables.overdueCount === 1 ? '' : 's'} overdue` : null,
    decideCount > 0 ? `${decideCount} to decide` : null,
  ].filter(Boolean).join(' · ');

  const vitals: FeedVitals = {
    recvTotal, recvOverdue, recvOverdueCount,
    payDue: payables.due, payOverdueCount: payables.overdueCount,
    toReply: visibleEmailThreads, awaitingClients: waitingOnThem.length,
    decideCount, summaryBits,
  };

  return { items, vitals };
}
